import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Lock } from 'lucide-react';
import StockItemSearch from './StockItemSearch';
import { toast } from 'sonner';
import axios from 'axios';
import useAuthStore from '@/store/authStore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Damage logging dialog reused by both pages. The parent picks `itemType`
 * ("RM" or "FG") — the StockItemSearch component already understands both.
 *
 * Inventory deduction happens server-side in POST /api/damage-logs (see
 * routes/damage_routes.py). The component is purely a typed form + the
 * "X units will be deducted from inventory" preview the user asked for.
 */
export default function LogDamageDialog({
  open,
  onOpenChange,
  itemType,            // "RM" | "FG"
  branches = [],       // [{branch_id, name}]
  defaultBranch = '',  // branch name to pre-select
  onLogged,            // fn(logResp) — called after successful POST so parent can refresh
}) {
  const [branch, setBranch] = useState(defaultBranch);
  const [selectedItem, setSelectedItem] = useState(null); // { item_id, name, current_stock }
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const hasBranchAccess = useAuthStore((s) => s.hasBranchAccess);

  useEffect(() => {
    if (open) {
      // Don't auto-fill a branch the user can't actually access — that would
      // submit the form with a locked branch and surprise them with a 403.
      const candidate = defaultBranch || '';
      const safe = candidate && hasBranchAccess(candidate) ? candidate : '';
      setBranch(safe);
      setSelectedItem(null);
      setQuantity('');
      setReason('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Resetting the item when branch changes prevents a stale pick from a
  // different branch being submitted.
  useEffect(() => {
    setSelectedItem(null);
    setQuantity('');
  }, [branch]);

  const qtyNum = parseFloat(quantity);
  const stock = selectedItem?.current_stock || 0;
  const exceedsStock = !Number.isNaN(qtyNum) && qtyNum > stock;
  const canSubmit = !!branch && !!selectedItem && qtyNum > 0 && !exceedsStock && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const branchObj = branches.find((b) => (b.name || b.branch_name) === branch);
      const resp = await axios.post(
        `${API}/damage-logs`,
        {
          item_type: itemType,
          item_id: selectedItem.item_id,
          branch,
          branch_id: branchObj?.branch_id || null,
          quantity: qtyNum,
          reason: reason || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Damage logged: ${qtyNum} unit${qtyNum === 1 ? '' : 's'} of ${selectedItem.item_id} deducted from ${branch}.`
      );
      onOpenChange(false);
      if (onLogged) onLogged(resp.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to log damage';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const itemLabel = itemType === 'RM' ? 'Raw Material' : 'Finished Good';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="damage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Log Damage — {itemLabel}
          </DialogTitle>
          <DialogDescription>
            This will deduct the damaged quantity from the selected branch's
            inventory and append an entry to the damage audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Branch */}
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Branch</Label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger data-testid="damage-branch-select">
                <SelectValue placeholder="Select a branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => {
                  const name = b.name || b.branch_name;
                  const allowed = hasBranchAccess(name);
                  return (
                    <SelectItem
                      key={b.branch_id || name}
                      value={name}
                      disabled={!allowed}
                      data-testid={`damage-branch-option-${name}`}
                    >
                      <span className="flex items-center gap-2">
                        {!allowed && <Lock className="w-3 h-3 text-zinc-400" />}
                        {name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Item search — only after a branch is picked */}
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">
              {itemLabel} (search by ID or name)
            </Label>
            <StockItemSearch
              branch={branch}
              type={itemType}
              value={selectedItem?.item_id || ''}
              onSelect={setSelectedItem}
              disabled={!branch}
              placeholder={branch ? `Search ${itemLabel.toLowerCase()}…` : 'Pick a branch first'}
              testId="damage-item-search"
            />
            {selectedItem && (
              <div className="text-xs text-zinc-600 mt-1" data-testid="damage-item-current-stock">
                Current stock in <span className="font-mono">{branch}</span>:{' '}
                <span className="font-mono font-bold">{stock}</span>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Damage Quantity</Label>
            <Input
              type="number"
              min="0.001"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={!selectedItem}
              placeholder={selectedItem ? 'Enter damaged quantity' : 'Pick an item first'}
              data-testid="damage-quantity-input"
            />
            {exceedsStock && (
              <p className="text-xs text-red-600 mt-1" data-testid="damage-exceeds-stock-error">
                Damage qty cannot exceed current stock ({stock}).
              </p>
            )}
          </div>

          {/* Reason (optional) */}
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What happened? (e.g., 'forklift drop', 'water damage')"
              rows={2}
              data-testid="damage-reason-textarea"
            />
          </div>

          {/* Confirmation preview the user asked for */}
          {qtyNum > 0 && selectedItem && !exceedsStock && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="damage-confirmation-preview"
            >
              <strong>{qtyNum}</strong> unit{qtyNum === 1 ? '' : 's'} of{' '}
              <code className="font-mono">{selectedItem.item_id}</code> will be{' '}
              <strong>deducted from inventory</strong> at{' '}
              <code className="font-mono">{branch}</code> (new balance:{' '}
              <strong>{(stock - qtyNum).toFixed(3).replace(/\.?0+$/, '')}</strong>).
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="damage-submit-btn"
          >
            {submitting ? 'Logging…' : 'Log Damage & Deduct'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
